/**
 * Leitura e validação da planilha de Inclusão em Lote (.xlsx, .xls ou .csv).
 * Documentação do formato: stoneup-client/doc/INCLUSAO_LOTE.md
 *
 * Tudo aqui é puro (sem Vue) para poder ser testado em tests/unit/inclusao/loteArquivo.spec.js.
 */

// SheetJS (~400 KB) só é carregado quando o usuário lê uma planilha ou baixa o modelo,
// para não pesar no chunk "about" compartilhado pelas outras páginas.
const carregarXlsx = () => import(/* webpackChunkName: "xlsx" */ "xlsx");

export const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

/** Colunas na ordem do modelo. `exemplo` e `formato` aparecem na aba de instruções do modelo. */
export const COLUNAS = [
  { campo: "nome", label: "Nome", obrigatoria: true, formato: "Nome completo ou razão social", exemplo: "Maria da Silva" },
  { campo: "data_nascimento", label: "Data de nascimento", formato: "dd/mm/aaaa", exemplo: "15/03/1980" },
  { campo: "cpf_cnpj", label: "CPF/CNPJ", obrigatoria: true, formato: "Com ou sem pontuação", exemplo: "529.982.247-25" },
  { campo: "rg", label: "RG", formato: "Opcional", exemplo: "1234567" },
  { campo: "endereco", label: "Endereço", obrigatoria: true, formato: "Logradouro", exemplo: "Rua das Flores" },
  { campo: "bairro", label: "Bairro", obrigatoria: true, exemplo: "Centro" },
  { campo: "numero", label: "Número", obrigatoria: true, formato: "Use S/N se não houver", exemplo: "123" },
  { campo: "municipio", label: "Município", obrigatoria: true, exemplo: "Salvador" },
  { campo: "uf", label: "UF", obrigatoria: true, formato: "Sigla com 2 letras", exemplo: "BA" },
  { campo: "cep", label: "CEP", obrigatoria: true, formato: "8 dígitos, com ou sem hífen", exemplo: "40000-000" },
  { campo: "telefone", label: "Telefone", formato: "DDD + número", exemplo: "(71) 99999-8888" },
  { campo: "email", label: "E-mail", exemplo: "maria@email.com" },
  { campo: "cliente_observacao", label: "Observação do cliente" },
  { campo: "numero_titulo", label: "Nº do título", obrigatoria: true, exemplo: "NF-1020" },
  { campo: "nosso_numero", label: "Nosso número", obrigatoria: true, exemplo: "000123" },
  { campo: "especie", label: "Espécie", obrigatoria: true, formato: "Sigla da espécie (veja a aba Espécies)", exemplo: "DM" },
  { campo: "valor_original", label: "Valor original", obrigatoria: true, formato: "Número, ex.: 1.234,56", exemplo: "1.234,56" },
  { campo: "valor_vencido", label: "Valor vencido", obrigatoria: true, formato: "Número, ex.: 1.300,00", exemplo: "1.300,00" },
  { campo: "data_emissao", label: "Data de emissão", obrigatoria: true, formato: "dd/mm/aaaa", exemplo: "05/01/2026" },
  { campo: "data_vencimento", label: "Data de vencimento", obrigatoria: true, formato: "dd/mm/aaaa, anterior a hoje", exemplo: "05/02/2026" },
  { campo: "divida_observacao", label: "Observação da dívida" },
  { campo: "coobrigado_nome", label: "Coobrigado: nome", formato: "Opcional. Se preencher, endereço completo e CPF/CNPJ dele passam a ser obrigatórios" },
  { campo: "coobrigado_data_nascimento", label: "Coobrigado: nascimento", formato: "dd/mm/aaaa" },
  { campo: "coobrigado_cpf_cnpj", label: "Coobrigado: CPF/CNPJ" },
  { campo: "coobrigado_rg", label: "Coobrigado: RG" },
  { campo: "coobrigado_endereco", label: "Coobrigado: endereço" },
  { campo: "coobrigado_bairro", label: "Coobrigado: bairro" },
  { campo: "coobrigado_numero", label: "Coobrigado: número" },
  { campo: "coobrigado_municipio", label: "Coobrigado: município" },
  { campo: "coobrigado_uf", label: "Coobrigado: UF" },
  { campo: "coobrigado_cep", label: "Coobrigado: CEP" },
  { campo: "coobrigado_telefone", label: "Coobrigado: telefone" },
  { campo: "coobrigado_email", label: "Coobrigado: e-mail" },
];

const LABELS = COLUNAS.reduce((acc, c) => ({ ...acc, [c.campo]: c.label }), {});

export function labelDoCampo(campo) {
  return LABELS[campo] || campo || "—";
}

const OBRIGATORIAS = COLUNAS.filter((c) => c.obrigatoria).map((c) => c.campo);

// Nomes alternativos aceitos no cabeçalho (depois de normalizados)
const APELIDOS = {
  cpf: "cpf_cnpj", cnpj: "cpf_cnpj", cpfcnpj: "cpf_cnpj", cpf_ou_cnpj: "cpf_cnpj", documento: "cpf_cnpj",
  logradouro: "endereco", cidade: "municipio", estado: "uf", e_mail: "email",
  coobrigado_cpf: "coobrigado_cpf_cnpj", coobrigado_cnpj: "coobrigado_cpf_cnpj", coobrigado_e_mail: "coobrigado_email",
};

export function normalizarCabecalho(texto) {
  const base = String(texto || "")
    .replace(/^\uFEFF/, "")
    .normalize("NFD").replace(/[\u0300-\u036F]/g, "")
    .trim().toLowerCase()
    .replace(/[\s\-/.]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_").replace(/^_|_$/g, "");
  return APELIDOS[base] || base;
}

/** Colunas obrigatórias ausentes no cabeçalho. */
export function colunasFaltando(cabecalho) {
  const presentes = new Set(cabecalho);
  return OBRIGATORIAS.filter((c) => !presentes.has(c));
}

// ───────────────────────── Leitura do arquivo ─────────────────────────

/** Decodifica o CSV: UTF-8 se for válido, senão Windows-1252 (padrão do Excel em português). */
export function decodificarTexto(buffer) {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch (e) {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

/** Parser de CSV com aspas, separador detectado (; , ou tab) e quebras de linha \r\n, \n ou \r. */
export function parseCsv(texto) {
  let conteudo = String(texto || "").replace(/^\uFEFF/, "");

  // Excel aceita uma primeira linha "sep=;" indicando o separador
  let separador = null;
  const sepLinha = conteudo.match(/^sep=(.)\r?\n/i);
  if (sepLinha) {
    separador = sepLinha[1];
    conteudo = conteudo.slice(sepLinha[0].length);
  }
  if (!separador) {
    const primeiraLinha = conteudo.split(/\r\n|\n|\r/, 1)[0].replace(/"[^"]*"/g, "");
    const contagem = [";", ",", "\t"].map((s) => [s, primeiraLinha.split(s).length - 1]);
    contagem.sort((a, b) => b[1] - a[1]);
    separador = contagem[0][1] > 0 ? contagem[0][0] : ";";
  }

  const linhas = [];
  let linha = [];
  let campo = "";
  let entreAspas = false;

  for (let i = 0; i < conteudo.length; i++) {
    const ch = conteudo[i];
    if (entreAspas) {
      if (ch === '"') {
        if (conteudo[i + 1] === '"') { campo += '"'; i++; } else { entreAspas = false; }
      } else {
        campo += ch;
      }
    } else if (ch === '"' && campo === "") {
      entreAspas = true;
    } else if (ch === separador) {
      linha.push(campo); campo = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && conteudo[i + 1] === "\n") i++;
      linha.push(campo); linhas.push(linha);
      linha = []; campo = "";
    } else {
      campo += ch;
    }
  }
  if (campo !== "" || linha.length > 0) {
    linha.push(campo);
    linhas.push(linha);
  }
  return linhas;
}

function dataTexto(d, m, a) {
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${a}`;
}

/** Texto de uma célula do Excel, sem perder dígitos (CNPJ não vira 1,23E+13) e com datas em dd/mm/aaaa. */
export function textoDaCelula(XLSX, cell) {
  if (!cell || cell.v === undefined || cell.v === null) return "";
  if (cell.t === "n") {
    if (cell.z && XLSX.SSF.is_date(cell.z)) {
      const d = XLSX.SSF.parse_date_code(cell.v);
      return dataTexto(d.d, d.m, d.y);
    }
    return Number.isInteger(cell.v) ? cell.v.toFixed(0) : String(cell.v);
  }
  if (cell.t === "d" && cell.v instanceof Date) {
    return dataTexto(cell.v.getDate(), cell.v.getMonth() + 1, cell.v.getFullYear());
  }
  return String(cell.v);
}

function linhasDaPlanilha(XLSX, buffer) {
  const wb = XLSX.read(buffer, { type: "array", cellNF: true, cellDates: false });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws["!ref"]) return [];
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const linhas = [];
  for (let r = range.s.r; r <= range.e.r; r++) {
    const linha = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      linha.push(textoDaCelula(XLSX, ws[XLSX.utils.encode_cell({ r, c })]));
    }
    linhas.push(linha);
  }
  return linhas;
}

/**
 * Lê o arquivo e devolve { cabecalho, registros }, onde cada registro é
 * { linha: <nº da linha no Excel>, valores: { campo: texto } }. Linhas totalmente vazias são ignoradas.
 */
export function interpretarLinhas(matriz) {
  const linhas = matriz.slice();
  // Pula linhas vazias antes do cabeçalho
  while (linhas.length && linhas[0].every((v) => String(v).trim() === "")) linhas.shift();
  const deslocamento = matriz.length - linhas.length;
  if (!linhas.length) return { cabecalho: [], registros: [] };

  const cabecalho = linhas[0].map(normalizarCabecalho);
  const registros = [];
  for (let i = 1; i < linhas.length; i++) {
    const celulas = linhas[i];
    if (celulas.every((v) => String(v).trim() === "")) continue;
    const valores = {};
    cabecalho.forEach((campo, j) => {
      if (campo && valores[campo] === undefined) valores[campo] = String(celulas[j] ?? "").trim();
    });
    registros.push({ linha: deslocamento + i + 1, valores });
  }
  return { cabecalho, registros };
}

export async function lerArquivo(arquivo) {
  const buffer = await arquivo.arrayBuffer();
  const nome = (arquivo.name || "").toLowerCase();
  const ehCsv = nome.endsWith(".csv") || nome.endsWith(".txt") || (arquivo.type || "").includes("csv");
  const matriz = ehCsv ? parseCsv(decodificarTexto(buffer)) : linhasDaPlanilha(await carregarXlsx(), buffer);
  return interpretarLinhas(matriz);
}

// ───────────────────────── Normalização ─────────────────────────

const ehNotacaoCientifica = (v) => /^\d+([.,]\d+)?e\+?\d+$/i.test(String(v).replace(/\s/g, ""));
const MSG_CIENTIFICA = "O Excel transformou o número em notação científica (ex.: 5,3E+10). Formate a coluna como Texto, digite o número de novo e salve.";

export function cpfValido(cpf) {
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;
  for (let t = 9; t < 11; t++) {
    let soma = 0;
    for (let c = 0; c < t; c++) soma += Number(cpf[c]) * (t + 1 - c);
    if (Number(cpf[t]) !== ((10 * soma) % 11) % 10) return false;
  }
  return true;
}

export function cnpjValido(cnpj) {
  if (!/^\d{14}$/.test(cnpj) || /^(\d)\1{13}$/.test(cnpj)) return false;
  for (const t of [12, 13]) {
    let soma = 0;
    let peso = t - 7;
    for (let c = 0; c < t; c++) {
      soma += Number(cnpj[c]) * peso;
      peso = peso === 2 ? 9 : peso - 1;
    }
    const digito = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (Number(cnpj[t]) !== digito) return false;
  }
  return true;
}

/** CPF/CNPJ só com dígitos, recolocando zeros à esquerda que o Excel remove. Devolve { valor } ou { erro }. */
export function normalizarDocumento(texto) {
  if (ehNotacaoCientifica(texto)) return { erro: MSG_CIENTIFICA };
  const digitos = String(texto).replace(/\D/g, "");
  if (!digitos) return { erro: "Informe o CPF ou CNPJ (apenas números ou com pontuação)." };
  if (digitos.length <= 11) {
    const cpf = digitos.padStart(11, "0");
    return cpfValido(cpf) ? { valor: cpf } : { erro: "CPF inválido: confira os dígitos." };
  }
  if (digitos.length <= 14) {
    const cnpj = digitos.padStart(14, "0");
    return cnpjValido(cnpj) ? { valor: cnpj } : { erro: "CNPJ inválido: confira os dígitos." };
  }
  return { erro: `Tem ${digitos.length} dígitos: CPF tem 11 e CNPJ tem 14.` };
}

/** Aceita d/m/aaaa, dd-mm-aaaa, dd.mm.aaaa, dd/mm/aa e aaaa-mm-dd. Devolve { valor: "dd/mm/aaaa", data } ou { erro }. */
export function normalizarData(texto) {
  const s = String(texto).trim();
  let d, m, a;
  let partes = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/);
  if (partes) {
    [, d, m, a] = partes;
    if (a.length === 2) {
      const anoAtual = new Date().getFullYear() % 100;
      a = (Number(a) <= anoAtual ? "20" : "19") + a;
    }
  } else if ((partes = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/))) {
    [, a, m, d] = partes;
  } else {
    return { erro: "Data inválida: use dd/mm/aaaa (ex.: 05/01/2026)." };
  }
  const data = new Date(Number(a), Number(m) - 1, Number(d));
  if (data.getFullYear() !== Number(a) || data.getMonth() !== Number(m) - 1 || data.getDate() !== Number(d)) {
    return { erro: `Data inexistente (${s}): confira dia e mês.` };
  }
  return { valor: dataTexto(d, m, a), data };
}

/** Aceita 1234,56 · 1.234,56 · 1234.56 · R$ 1.234,56. Devolve { valor: "1234.56" } ou { erro }. */
export function normalizarValor(texto) {
  let s = String(texto).replace(/R\$|\s/gi, "");
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if ((s.match(/\./g) || []).length > 1) {
    s = s.replace(/\./g, "");
  }
  if (!/^\d+(\.\d+)?$/.test(s) || Number(s) <= 0) {
    return { erro: "Valor inválido: use só números maiores que zero (ex.: 1.234,56)." };
  }
  return { valor: Number(s).toFixed(2) };
}

export function normalizarCep(texto) {
  if (ehNotacaoCientifica(texto)) return { erro: MSG_CIENTIFICA };
  const digitos = String(texto).replace(/\D/g, "");
  // CEP começando com 0 perde o zero quando o Excel trata a coluna como número
  if (digitos.length === 7) return { valor: digitos.padStart(8, "0") };
  if (digitos.length === 8) return { valor: digitos };
  return { erro: "CEP deve ter 8 dígitos (ex.: 01310-100)." };
}

export function normalizarTelefone(texto) {
  if (ehNotacaoCientifica(texto)) return { erro: MSG_CIENTIFICA };
  let digitos = String(texto).replace(/\D/g, "");
  if ((digitos.length === 12 || digitos.length === 13) && digitos.startsWith("55")) digitos = digitos.slice(2);
  if (digitos.length === 11 && digitos.startsWith("0")) digitos = digitos.slice(1);
  if (digitos.length === 10 || digitos.length === 11) return { valor: digitos };
  return { erro: "Telefone deve ter DDD + número (10 ou 11 dígitos), ex.: (71) 99999-8888." };
}

// ───────────────────────── Validação de uma linha ─────────────────────────

function hojeSemHora(hoje) {
  const h = hoje ? new Date(hoje) : new Date();
  return new Date(h.getFullYear(), h.getMonth(), h.getDate());
}

/**
 * Valida e normaliza uma linha da planilha.
 * @param valores   { campo: texto } da linha
 * @param especies  lista de getEspecies ({ id, sigla, ... })
 * @returns { dados, erros } — dados no formato esperado por sendLoteNegativar; erros: [{ campo, valor, mensagem }]
 */
export function validarRegistro(valores, especies, hoje) {
  const erros = [];
  const dados = {};
  const v = (campo) => String(valores[campo] ?? "").trim();
  const erro = (campo, mensagem) => erros.push({ campo, valor: v(campo), mensagem });
  const hojeData = hojeSemHora(hoje);

  const exigir = (campo, mensagem) => {
    if (!v(campo)) { erro(campo, mensagem || "Campo obrigatório não preenchido."); return false; }
    return true;
  };
  const aplicar = (campo, resultado) => {
    if (resultado.erro) erro(campo, resultado.erro);
    else dados[campo] = resultado.valor;
    return resultado;
  };

  const validarPessoa = (prefixo) => {
    const p = (campo) => prefixo + campo;

    if (exigir(p("nome"))) {
      const nome = v(p("nome")).replace(/\s+/g, " ");
      if (nome.replace(/[^A-Za-zÀ-ÿ]/g, "").length < 3) erro(p("nome"), "Nome muito curto: informe o nome completo.");
      else dados[p("nome")] = nome;
    }
    if (exigir(p("cpf_cnpj"))) aplicar(p("cpf_cnpj"), normalizarDocumento(v(p("cpf_cnpj"))));

    if (v(p("data_nascimento"))) {
      const nasc = normalizarData(v(p("data_nascimento")));
      if (!nasc.erro && nasc.data >= hojeData) erro(p("data_nascimento"), "Data de nascimento não pode ser hoje ou futura.");
      else aplicar(p("data_nascimento"), nasc);
    }
    if (v(p("rg"))) {
      if (v(p("rg")).replace(/[^0-9A-Za-z]/g, "").length < 3) erro(p("rg"), "RG muito curto (mínimo 3 caracteres). Deixe em branco se não tiver.");
      else dados[p("rg")] = v(p("rg"));
    }

    ["endereco", "bairro", "municipio"].forEach((c) => { if (exigir(p(c))) dados[p(c)] = v(p(c)); });
    if (exigir(p("numero"), "Informe o número (use S/N se não houver).")) dados[p("numero")] = v(p("numero"));

    if (exigir(p("uf"))) {
      const uf = v(p("uf")).toUpperCase();
      if (!UFS.includes(uf)) erro(p("uf"), "UF inválida: use a sigla com 2 letras (ex.: SP, BA).");
      else dados[p("uf")] = uf;
    }
    if (exigir(p("cep"))) aplicar(p("cep"), normalizarCep(v(p("cep"))));

    if (v(p("telefone"))) aplicar(p("telefone"), normalizarTelefone(v(p("telefone"))));
    if (v(p("email"))) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v(p("email")))) erro(p("email"), "E-mail em formato inválido (ex.: nome@empresa.com).");
      else dados[p("email")] = v(p("email"));
    }
  };

  // Devedor
  validarPessoa("");
  if (v("cliente_observacao")) dados.cliente_observacao = v("cliente_observacao");

  // Dívida
  ["numero_titulo", "nosso_numero"].forEach((c) => {
    if (!exigir(c)) return;
    if (ehNotacaoCientifica(v(c))) erro(c, MSG_CIENTIFICA);
    else dados[c] = v(c);
  });

  if (exigir("especie")) {
    const informada = v("especie").toUpperCase();
    const especie = (especies || []).find((e) => String(e.sigla || "").trim().toUpperCase() === informada || String(e.id) === informada);
    if (especie) {
      dados.especie = especie.id;
    } else {
      const siglas = (especies || []).map((e) => e.sigla).filter(Boolean);
      erro("especie", `Espécie "${v("especie")}" não existe.` + (siglas.length ? ` Use uma destas siglas: ${siglas.join(", ")}.` : ""));
    }
  }

  if (exigir("valor_original")) aplicar("valor_original", normalizarValor(v("valor_original")));
  if (exigir("valor_vencido")) aplicar("valor_vencido", normalizarValor(v("valor_vencido")));

  const emissao = exigir("data_emissao") ? aplicar("data_emissao", normalizarData(v("data_emissao"))) : {};
  const vencimento = exigir("data_vencimento") ? aplicar("data_vencimento", normalizarData(v("data_vencimento"))) : {};
  if (vencimento.data && vencimento.data >= hojeData) {
    erro("data_vencimento", "A dívida ainda não venceu: o vencimento deve ser anterior a hoje.");
    delete dados.data_vencimento;
  }
  if (emissao.data && emissao.data > hojeData) {
    erro("data_emissao", "Data de emissão não pode ser futura.");
    delete dados.data_emissao;
  } else if (emissao.data && vencimento.data && emissao.data > vencimento.data) {
    erro("data_emissao", "Data de emissão é posterior à data de vencimento.");
    delete dados.data_emissao;
  }
  if (v("divida_observacao")) dados.divida_observacao = v("divida_observacao");

  // Coobrigado (opcional)
  const camposCoobrigado = COLUNAS.map((c) => c.campo).filter((c) => c.startsWith("coobrigado_"));
  if (v("coobrigado_nome")) {
    validarPessoa("coobrigado_");
  } else if (camposCoobrigado.some((c) => v(c))) {
    erro("coobrigado_nome", "Há dados de coobrigado nesta linha, mas sem o nome. Preencha o nome ou apague os outros dados do coobrigado.");
  }

  return { dados, erros };
}

// ───────────────────────── Arquivos gerados ─────────────────────────

function celulaCsv(valor) {
  const s = String(valor ?? "");
  return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV (; e BOM, abre direto no Excel) com a lista de erros. */
export function relatorioErrosCsv(erros) {
  const linhas = [["Linha", "Coluna", "Valor informado", "O que corrigir"]].concat(
    erros.map((e) => [e.linha ?? "", labelDoCampo(e.campo), e.valor ?? "", e.mensagem])
  );
  return "\uFEFF" + linhas.map((l) => l.map(celulaCsv).join(";")).join("\r\n");
}

/** Planilha modelo: aba de dados com todas as colunas formatadas como Texto + abas de instruções e espécies. */
export function montarModelo(XLSX, especies) {
  const LINHAS_FORMATADAS = 500;
  const dados = XLSX.utils.aoa_to_sheet([COLUNAS.map((c) => c.campo)]);
  for (let r = 1; r <= LINHAS_FORMATADAS; r++) {
    COLUNAS.forEach((c, j) => {
      dados[XLSX.utils.encode_cell({ r, c: j })] = { t: "s", v: "", z: "@" };
    });
  }
  dados["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: LINHAS_FORMATADAS, c: COLUNAS.length - 1 } });
  dados["!cols"] = COLUNAS.map((c) => ({ wch: Math.max(c.campo.length + 2, 14) }));

  const instrucoes = XLSX.utils.aoa_to_sheet([
    ["Como preencher: uma linha por título, a partir da linha 2 da aba Negativacoes. Não altere a linha 1 (cabeçalho)."],
    [],
    ["Coluna", "Obrigatória", "Formato", "Exemplo"],
    ...COLUNAS.map((c) => [c.campo, c.obrigatoria ? "Sim" : "Não", c.formato || "", c.exemplo || ""]),
  ]);
  instrucoes["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 60 }, { wch: 22 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, dados, "Negativacoes");
  XLSX.utils.book_append_sheet(wb, instrucoes, "Instrucoes");

  if (especies && especies.length) {
    const abaEspecies = XLSX.utils.aoa_to_sheet([
      ["Sigla", "Descrição"],
      ...especies.map((e) => [e.sigla, e.descricao || ""]),
    ]);
    abaEspecies["!cols"] = [{ wch: 10 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, abaEspecies, "Especies");
  }
  return wb;
}

export async function baixarModelo(especies) {
  const XLSX = await carregarXlsx();
  XLSX.writeFile(montarModelo(XLSX, especies), "Modelo Inclusao em Lote.xlsx");
}
